"use client";

// CONFY organizers landing – Variant 8: «Конструктор»
// Modular building blocks: your event assembled from modules like a
// well-made construction kit. Strict grid, 2px ink borders, module color
// system, Rubik + JetBrains Mono. All copy Ukrainian.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  motion,
  AnimatePresence,
  animate,
  useMotionValue,
  useInView,
} from "motion/react";
import confetti from "canvas-confetti";
import { posthog } from "@/lib/analytics/posthog";

// Design-review build: animations forced ON so every reviewer sees the full
// motion design even with prefers-reduced-motion enabled (also keeps SSR and
// client renders identical). For production, restore:
//   import { useReducedMotion } from "motion/react";
const useReducedMotion = () => false;
import { Rubik, JetBrains_Mono } from "next/font/google";
import {
  ArrowRight,
  ArrowDown,
  Check,
  Plus,
  Wallet,
  Clock,
  Mail,
  Play,
  RotateCcw,
  Volume2,
  X,
} from "lucide-react";
import {
  HERO,
  FREE_OFFER,
  WHY_ITEMS,
  CORE_FEATURES,
  TIERS,
  ADDONS,
  PRO_PLAN,
  WALLET,
  AUTOSCALE,
  FUTURE_ADDONS,
  CONTACT,
  formatUAH,
  estimateTotal,
  type Tier,
  type Addon,
  type EstimatorAddonState,
} from "./_data";

// ---------------------------------------------------------------------------
// Fonts (module scope, variable fonts – no weight needed)
// ---------------------------------------------------------------------------

const rubik = Rubik({ subsets: ["latin", "cyrillic"] });
const jetMono = JetBrains_Mono({ subsets: ["latin", "cyrillic"] });

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

const INK = "#16161C";
const GROUND = "#F3F4F6";

type ModuleMeta = { color: string; num: string; text: string };

const MODULES: Record<string, ModuleMeta> = {
  catalog: { color: "#2F5BEA", num: "01", text: "#FFFFFF" },
  payments: { color: "#2FC98F", num: "02", text: INK },
  bpr: { color: "#FFB02E", num: "03", text: INK },
  marketing: { color: "#64748B", num: "04", text: "#FFFFFF" },
};

const MODULE_COLOR_CYCLE = [
  "#2F5BEA",
  "#FF6B57",
  "#2FC98F",
  "#FFB02E",
  "#8B5CF6",
  "#64748B",
];

// One color per tier so the five size bricks read as a progression, not a wall
// of identical ink blocks (free tier matches its amber «Старт без ризику» badge).
const TIER_COLORS = ["#FFB02E", "#2FC98F", "#2F5BEA", "#8B5CF6", INK];

const focusCls =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2F5BEA]";

const cardShadow = "0 6px 16px rgba(22,22,28,0.08)";

function tint(color: string, pct: number): string {
  return `color-mix(in srgb, ${color} ${pct}%, white)`;
}

// ---------------------------------------------------------------------------
// Organizer activation gateway
// Every «Створити подію» CTA runs this flow instead of a plain link:
//   anonymous → /login (returnUrl brings them back and auto-resumes);
//   participant → role becomes organizer + celebration modal;
//   organizer/admin → straight to the event builder.
// ---------------------------------------------------------------------------

const BUILDER_URL =
  "/event/builder?utm_source=organizers_landing&utm_medium=cta&utm_campaign=organizer_activation";

type ActivationState = "idle" | "loading" | "celebrate" | "error";

const ActivationCtx = createContext<{
  state: ActivationState;
  start: () => void;
} | null>(null);

function useActivation() {
  const ctx = useContext(ActivationCtx);
  if (!ctx) throw new Error("useActivation outside provider");
  return ctx;
}

function fireConfetti() {
  const colors = ["#2F5BEA", "#FF6B57", "#2FC98F", "#FFB02E", "#8B5CF6"];
  confetti({ particleCount: 90, spread: 75, origin: { y: 0.6 }, colors });
  setTimeout(
    () =>
      confetti({
        particleCount: 60,
        angle: 60,
        spread: 60,
        origin: { x: 0, y: 0.7 },
        colors,
      }),
    250
  );
  setTimeout(
    () =>
      confetti({
        particleCount: 60,
        angle: 120,
        spread: 60,
        origin: { x: 1, y: 0.7 },
        colors,
      }),
    450
  );
}

// ---------------------------------------------------------------------------
// Free-offer gate – the «10 free events» promo is shown only with ?free=on;
// the default page never mentions it. Marketing routing, not access control.
// ---------------------------------------------------------------------------

const FreeOfferCtx = createContext(false);
const useFreeOffer = () => useContext(FreeOfferCtx);

function FreeOfferProvider({ children }: { children: React.ReactNode }) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("free") !== "on") return;
    // Deferred a frame to avoid a sync setState in the effect body. Runs after
    // ActivationProvider's child effect, which strips only the activate param.
    const id = requestAnimationFrame(() => setEnabled(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return <FreeOfferCtx.Provider value={enabled}>{children}</FreeOfferCtx.Provider>;
}

function ActivationProvider({ children }: { children: React.ReactNode }) {
  const freeOffer = useFreeOffer();
  const [state, setState] = useState<ActivationState>("idle");
  const loadingRef = useRef(false);

  const start = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setState("loading");
    try {
      const res = await fetch("/api/organizer/activate", { method: "POST" });
      if (res.status === 401) {
        // Preserve the current variant params (free/welcome) across login.
        const params = new URLSearchParams(window.location.search);
        params.set("activate", "1");
        const returnUrl = `${window.location.pathname}?${params.toString()}`;
        window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
        return;
      }
      const data = (await res.json()) as { status?: string };
      if (data.status === "activated") {
        posthog?.capture("organizer_activated", {
          source: "organizers_landing",
        });
        setState("celebrate");
        fireConfetti();
      } else if (data.status === "already") {
        window.location.href = BUILDER_URL;
      } else {
        setState("error");
      }
    } catch {
      setState("error");
    } finally {
      loadingRef.current = false;
    }
  }, []);

  // Post-login return: …?activate=1 resumes the flow automatically. Only the
  // activate param is stripped – free/welcome variant params must survive.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("activate") === "1") {
      params.delete("activate");
      const qs = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (qs ? `?${qs}` : "")
      );
      void start();
    }
  }, [start]);

  const close = () => setState("idle");

  return (
    <ActivationCtx.Provider value={{ state, start }}>
      {children}
      <AnimatePresence>
        {(state === "celebrate" || state === "error") && (
          <motion.div
            key="activation-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={
              state === "celebrate"
                ? "Вітаємо, ви тепер організатор"
                : "Не вдалося активувати роль організатора"
            }
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onKeyDown={(e) => {
              if (e.key === "Escape") close();
            }}
          >
            <button
              type="button"
              aria-label="Закрити"
              className="absolute inset-0 bg-[#16161C]/55"
              onClick={close}
            />
            <motion.div
              className="relative w-full max-w-md rounded-[18px] border-2 bg-white p-7 sm:p-9"
              style={{ borderColor: INK, boxShadow: "6px 8px 0 rgba(22,22,28,0.2)" }}
              initial={{ y: 40, scale: 0.94, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 20, scale: 0.96, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 24 }}
            >
              {state === "celebrate" ? (
                <>
                  <div className="mb-5 flex justify-center">
                    <Brick
                      color="#2FC98F"
                      textColor={INK}
                      width={210}
                      height={44}
                      label="Роль: організатор"
                      price="✓"
                    />
                  </div>
                  <h3 className="text-center text-2xl font-bold leading-tight">
                    Вітаємо – ви тепер організатор!
                  </h3>
                  {freeOffer ? (
                    <p className="mt-3 text-center text-[15px] leading-relaxed text-[#4B4B55]">
                      <span className="font-bold text-[#16161C]">
                        Перші 10 подій – безкоштовно до кінця 2026.
                      </span>{" "}
                      Все включено: до 50 000 реєстрацій на подію та всі модулі
                      платформи – без доплат.
                    </p>
                  ) : (
                    <p className="mt-3 text-center text-[15px] leading-relaxed text-[#4B4B55]">
                      Вам доступний конструктор подій: реєстрація, тестування,
                      сертифікати та БПР – усе на одній платформі.
                    </p>
                  )}
                  <div className="mt-7 flex flex-col items-center gap-3">
                    <a
                      href={BUILDER_URL}
                      target="_blank"
                      rel="noopener"
                      className={`inline-flex w-full items-center justify-center gap-2 rounded-full border-2 px-6 py-3.5 text-base font-bold text-white transition-transform hover:-translate-y-0.5 ${focusCls}`}
                      style={{ borderColor: INK, background: INK, boxShadow: cardShadow }}
                    >
                      Створити першу подію
                      <ArrowRight className="h-5 w-5" strokeWidth={2} />
                    </a>
                    <button
                      type="button"
                      onClick={close}
                      className={`text-sm font-semibold text-[#4B4B55] underline underline-offset-2 ${focusCls} rounded-sm`}
                    >
                      Пізніше
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-center text-xl font-bold leading-tight">
                    Щось пішло не так
                  </h3>
                  <p className="mt-3 text-center text-[15px] leading-relaxed text-[#4B4B55]">
                    Не вдалося активувати роль організатора. Спробуйте ще раз або
                    напишіть нам:{" "}
                    <a
                      href={`mailto:${CONTACT.email}`}
                      className="font-semibold underline"
                    >
                      {CONTACT.email}
                    </a>
                  </p>
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={() => void start()}
                      className={`inline-flex items-center gap-2 rounded-full border-2 px-6 py-3 text-sm font-bold text-white ${focusCls}`}
                      style={{ borderColor: INK, background: INK }}
                    >
                      Спробувати ще раз
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ActivationCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// Welcome video – shown only when the page is opened with ?welcome=on
// ---------------------------------------------------------------------------

const WELCOME_VIDEO_URL =
  "https://courses3.confy.cc/storage/v1/object/public/misc/welcome/konfik-vitaye.mp4";

// Loud enough to sound intentional on laptop speakers, quiet enough not to
// startle anyone in a public space.
const VIDEO_VOLUME = 0.6;

function WelcomeVideoModal() {
  const [open, setOpen] = useState(false);
  // Autoplay policy gates on user activation, which a document loaded straight
  // from ?welcome=on never has – so unmuted play() is refused everywhere except
  // high-MEI Chrome. We ask for sound first, then fall back to muted playback
  // (allowed universally) plus a prominent unmute pill, so the video is always
  // moving and sound is one tap away.
  const [overlay, setOverlay] = useState<"none" | "tap" | "unmute" | "ended">(
    "none",
  );
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("welcome") !== "on") return;
    // Deferred a frame: avoids a sync setState in the effect body and lets the
    // page paint once before the overlay animates in.
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    const v = videoRef.current;
    if (!v) return;
    // Ignored on iOS, where volume is hardware-only; polite level elsewhere.
    v.volume = VIDEO_VOLUME;
    v.muted = false;
    v.play()
      .then(() => setOverlay("none"))
      .catch(() => {
        v.muted = true;
        v.play()
          .then(() => setOverlay("unmute"))
          // Even muted autoplay can be refused (iOS Low Power Mode).
          .catch(() => setOverlay("tap"));
      });
  }, [open]);

  // Every overlay button is a real user gesture, so sound is always grantable.
  const playFromStart = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.volume = VIDEO_VOLUME;
    v.currentTime = 0;
    setOverlay("none");
    v.play().catch(() => setOverlay("tap"));
  };

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="welcome-video-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Вітальне відео"
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onKeyDown={(e) => {
            if (e.key === "Escape") close();
          }}
        >
          <button
            type="button"
            aria-label="Закрити"
            className="absolute inset-0 bg-[#16161C]/60 backdrop-blur-md"
            onClick={close}
          />
          <motion.div
            className="relative w-full max-w-2xl rounded-[18px] border-2 bg-white p-3 sm:p-4"
            style={{ borderColor: INK, boxShadow: "6px 8px 0 rgba(22,22,28,0.2)" }}
            initial={{ y: 40, scale: 0.94, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
          >
            <div className="mb-2.5 flex items-center justify-between gap-3 pl-2">
              <p className="text-base font-bold sm:text-lg">Конфік вітає вас!</p>
              <button
                type="button"
                aria-label="Закрити"
                onClick={close}
                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 bg-white transition-transform hover:-translate-y-0.5 ${focusCls}`}
                style={{ borderColor: INK }}
              >
                <X className="h-4.5 w-4.5" strokeWidth={2.5} />
              </button>
            </div>
            <div
              className="relative overflow-hidden rounded-xl border-2 bg-black"
              style={{ borderColor: INK }}
            >
              <video
                ref={videoRef}
                src={WELCOME_VIDEO_URL}
                className="block max-h-[70vh] w-full"
                controls
                playsInline
                preload="auto"
                onEnded={() => setOverlay("ended")}
                onVolumeChange={() => {
                  // Unmuting through the native controls retires our pill.
                  if (!videoRef.current?.muted) {
                    setOverlay((o) => (o === "unmute" ? "none" : o));
                  }
                }}
              />
              <VideoOverlay mode={overlay} onActivate={playFromStart} />
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-end gap-3 px-1 pb-1">
              <button
                type="button"
                onClick={close}
                className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 ${focusCls}`}
                style={{ borderColor: INK, background: INK }}
              >
                Продовжити
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * The three states that sit on top of the welcome video:
 * `unmute` rides over live playback so the video stays fully visible, while
 * `tap` and `ended` dim a still frame behind a circular control.
 */
function VideoOverlay({
  mode,
  onActivate,
}: {
  mode: "none" | "tap" | "unmute" | "ended";
  onActivate: () => void;
}) {
  if (mode === "none") return null;

  if (mode === "unmute") {
    return (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <motion.button
          type="button"
          onClick={onActivate}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 380, damping: 22 }}
          className={`pointer-events-auto inline-flex items-center gap-2 rounded-full border-2 bg-white px-5 py-3 text-sm font-bold transition-transform hover:-translate-y-0.5 sm:px-6 sm:text-base ${focusCls}`}
          style={{ borderColor: INK, boxShadow: cardShadow }}
        >
          <Volume2 className="h-5 w-5" strokeWidth={2.5} />
          Увімкнути звук
        </motion.button>
      </div>
    );
  }

  const ended = mode === "ended";
  return (
    // The dim is click-through so the native controls underneath stay usable –
    // only the circle takes the pointer.
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#16161C]/45">
      <button
        type="button"
        aria-label={ended ? "Переглянути ще раз" : "Відтворити відео"}
        onClick={onActivate}
        className={`pointer-events-auto inline-flex h-16 w-16 items-center justify-center rounded-full border-2 bg-white transition-transform hover:-translate-y-0.5 ${focusCls}`}
        style={{ borderColor: INK, boxShadow: cardShadow }}
      >
        {ended ? (
          <RotateCcw className="h-7 w-7" strokeWidth={2.5} />
        ) : (
          <Play className="ml-1 h-7 w-7" strokeWidth={2.5} />
        )}
      </button>
    </div>
  );
}

/** CTA button that runs the organizer-activation flow. Keeps link styling. */
function ActivationCta({
  className,
  style,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const { state, start } = useActivation();
  return (
    <button
      type="button"
      onClick={() => void start()}
      disabled={state === "loading"}
      className={`${className ?? ""} disabled:cursor-wait disabled:opacity-70`}
      style={style}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// FreeOfferSticker – irregular gold starburst pinned over section corners.
// Every radius/angle is hand-jittered (deterministic – no Math.random, so SSR
// and client render the identical shape) to read as a cut-out price sticker
// rather than a geometric star. Clicking scrolls to the #free-offer details.
// ---------------------------------------------------------------------------

const STICKER_SPIKES = 14;
// prettier-ignore
const STICKER_OUTER_R = [0.98, 0.9, 1, 0.93, 0.97, 0.88, 1, 0.94, 0.9, 0.99, 0.92, 1, 0.9, 0.96];
// prettier-ignore
const STICKER_INNER_R = [0.68, 0.72, 0.66, 0.7, 0.67, 0.73, 0.68, 0.71, 0.66, 0.7, 0.72, 0.67, 0.7, 0.68];
// prettier-ignore
const STICKER_ANGLE_JITTER = [3, -4, 2, 5, -3, 1, -5, 4, -2, 3, -4, 2, -3, 4];

const STICKER_POINTS = (() => {
  const pts: string[] = [];
  const step = 360 / STICKER_SPIKES;
  for (let i = 0; i < STICKER_SPIKES; i++) {
    const outerA = ((i * step + STICKER_ANGLE_JITTER[i]) * Math.PI) / 180;
    const innerA =
      ((i * step + step / 2 + STICKER_ANGLE_JITTER[(i + 1) % STICKER_SPIKES]) *
        Math.PI) /
      180;
    const or = 97 * STICKER_OUTER_R[i];
    const ir = 97 * STICKER_INNER_R[i];
    pts.push(
      `${(100 + or * Math.cos(outerA)).toFixed(1)},${(100 + or * Math.sin(outerA)).toFixed(1)}`
    );
    pts.push(
      `${(100 + ir * Math.cos(innerA)).toFixed(1)},${(100 + ir * Math.sin(innerA)).toFixed(1)}`
    );
  }
  return pts.join(" ");
})();

function FreeOfferSticker({ className }: { className?: string }) {
  const freeOffer = useFreeOffer();
  const reduced = useReducedMotion();
  const gradId = `sticker-grad-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  if (!freeOffer) return null;
  return (
    <motion.a
      href="#free-offer"
      aria-label="Перші 10 подій – безкоштовно для нових провайдерів. Перейти до деталей пропозиції"
      className={`absolute z-10 block ${focusCls} rounded-full ${className ?? ""}`}
      initial={false}
      style={{ rotate: 8 }}
      animate={
        reduced
          ? undefined
          : { rotate: [8, 11.5, 6.5, 10, 8], scale: [1, 1.04, 0.99, 1.05, 1] }
      }
      transition={{
        // Two out-of-phase loops with unequal durations so the motion never
        // repeats visibly – a slow, slightly irregular float.
        rotate: {
          duration: 9.5,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.26, 0.57, 0.81, 1],
        },
        scale: {
          duration: 6.8,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.31, 0.52, 0.78, 1],
        },
      }}
      whileHover={{ scale: 1.12, rotate: 3 }}
      whileTap={{ scale: 0.96 }}
    >
      <svg
        viewBox="0 0 200 200"
        className="h-auto w-full"
        style={{ filter: "drop-shadow(0 8px 14px rgba(22,22,28,0.22))" }}
        aria-hidden
      >
        <defs>
          <radialGradient id={gradId} cx="38%" cy="32%" r="80%">
            <stop offset="0%" stopColor="#FFD976" />
            <stop offset="55%" stopColor="#FFC24A" />
            <stop offset="100%" stopColor="#FFA51F" />
          </radialGradient>
        </defs>
        <polygon
          points={STICKER_POINTS}
          fill={`url(#${gradId})`}
          stroke={INK}
          strokeWidth={3}
          strokeLinejoin="round"
        />
        <text
          x="100"
          y="78"
          textAnchor="middle"
          fill={INK}
          fontSize="15"
          fontWeight={700}
          letterSpacing="1.5"
        >
          ПЕРШІ
        </text>
        <text
          x="100"
          y="106"
          textAnchor="middle"
          fill={INK}
          fontSize="24"
          fontWeight={800}
          textLength="106"
          lengthAdjust="spacingAndGlyphs"
        >
          10 ПОДІЙ
        </text>
        <text
          x="100"
          y="128"
          textAnchor="middle"
          fill={INK}
          fontSize="13.5"
          fontWeight={700}
          textLength="114"
          lengthAdjust="spacingAndGlyphs"
        >
          БЕЗКОШТОВНО*
        </text>
      </svg>
    </motion.a>
  );
}

// ---------------------------------------------------------------------------
// Brick – a 2.5D building block (front face + top + right faces via skew)
// ---------------------------------------------------------------------------

type BrickProps = {
  color: string;
  width: number | string;
  height: number;
  depth?: number;
  textColor?: string;
  label?: string;
  price?: string;
  className?: string;
};

function Brick({
  color,
  width,
  height,
  depth = 11,
  textColor = "#FFFFFF",
  label,
  price,
  className,
}: BrickProps) {
  return (
    <div
      className={className}
      style={{ width, maxWidth: "100%", paddingTop: depth, paddingRight: depth }}
    >
      <div className="relative" style={{ height }}>
        {/* top face */}
        <div
          aria-hidden
          className="absolute left-0 top-0 w-full"
          style={{
            height: depth,
            transform: `translateY(-${depth}px) skewX(-45deg)`,
            transformOrigin: "bottom left",
            background: tint(color, 55),
            border: `2px solid ${INK}`,
            borderBottom: "none",
          }}
        />
        {/* right face */}
        <div
          aria-hidden
          className="absolute top-0"
          style={{
            right: -depth,
            width: depth,
            height: "100%",
            transform: "skewY(-45deg)",
            transformOrigin: "top left",
            background: `color-mix(in srgb, ${color} 64%, ${INK})`,
            border: `2px solid ${INK}`,
            borderLeft: "none",
          }}
        />
        {/* front face */}
        <div
          className="relative flex h-full items-center justify-between gap-2 overflow-hidden px-3"
          style={{ background: color, border: `2px solid ${INK}`, color: textColor }}
        >
          {label ? (
            <span className="truncate text-[13px] font-semibold leading-tight">
              {label}
            </span>
          ) : null}
          {price ? (
            <span
              className={`${jetMono.className} shrink-0 text-[12px] font-medium`}
            >
              {price}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reveal – rise+fade once in view; everything stays visible if observers fail
// ---------------------------------------------------------------------------

function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// CountUp – renders final value on SSR, counts up when scrolled into view
// ---------------------------------------------------------------------------

function formatAudience(n: number): string {
  return n.toLocaleString("uk-UA").replace(/ /g, " ") + "+";
}

function CountUp({
  to,
  format,
  className,
}: {
  to: number;
  format: (n: number) => string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduced = useReducedMotion();
  const [text, setText] = useState(() => format(to));

  useEffect(() => {
    if (!inView || reduced) return;
    const controls = animate(0, to, {
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setText(format(Math.round(v))),
    });
    return () => controls.stop();
  }, [inView, reduced, to, format]);

  return (
    <span ref={ref} className={className}>
      {text}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Section shell
// ---------------------------------------------------------------------------

function SectionHeader({
  kicker,
  title,
  sub,
}: {
  kicker: string;
  title: string;
  sub?: string;
}) {
  return (
    <Reveal className="mx-auto mb-10 max-w-3xl text-center sm:mb-14">
      <p
        className={`${jetMono.className} mb-3 text-[12px] font-medium uppercase tracking-[0.18em] text-[#64748B]`}
      >
        {kicker}
      </p>
      <h2 className="text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
        {title}
      </h2>
      {sub ? (
        <p className="mt-4 text-base leading-relaxed text-[#4B4B55] sm:text-lg">
          {sub}
        </p>
      ) : null}
    </Reveal>
  );
}

// ---------------------------------------------------------------------------
// Top navigation
// ---------------------------------------------------------------------------

function TopNav() {
  return (
    <header
      className="sticky top-0 z-50 border-b-2 bg-white/92 backdrop-blur"
      style={{ borderColor: INK }}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <a href="#top" className={`flex items-center gap-2.5 ${focusCls} rounded-md`}>
          <span aria-hidden className="flex items-end gap-[3px]">
            <span
              className="block h-3 w-3 rounded-[3px] border-2"
              style={{ borderColor: INK, background: "#2F5BEA" }}
            />
            <span
              className="block h-4 w-3 rounded-[3px] border-2"
              style={{ borderColor: INK, background: "#FFB02E" }}
            />
            <span
              className="block h-2.5 w-3 rounded-[3px] border-2"
              style={{ borderColor: INK, background: "#2FC98F" }}
            />
          </span>
          <span className="text-lg font-extrabold tracking-tight">CONFY</span>
        </a>
        <nav aria-label="Основна навігація" className="hidden items-center gap-6 md:flex">
          <a href="#why" className={`text-sm font-medium hover:underline ${focusCls} rounded-md`}>
            Можливості
          </a>
          <a href="#tiers" className={`text-sm font-medium hover:underline ${focusCls} rounded-md`}>
            Тарифи
          </a>
          <a href="#addons" className={`text-sm font-medium hover:underline ${focusCls} rounded-md`}>
            Модулі
          </a>
          {/* Temporarily hidden with the PRO section:
          <a href="#pro" className={`text-sm font-medium hover:underline ${focusCls} rounded-md`}>
            PRO
          </a> */}
        </nav>
        <ActivationCta
          className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 sm:px-5 ${focusCls}`}
          style={{ borderColor: INK, background: INK, boxShadow: cardShadow }}
        >
          Створити подію
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </ActivationCta>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Hero – copy + decorative pre-assembled stack (one ambient hop)
// ---------------------------------------------------------------------------

function HeroStack() {
  const reduced = useReducedMotion();
  return (
    <div
      className="relative rounded-[18px] border-2 p-6 sm:p-8"
      style={{
        borderColor: INK,
        background: GROUND,
        boxShadow: cardShadow,
        backgroundImage: "radial-gradient(#D8DAE1 1.2px, transparent 1.2px)",
        backgroundSize: "22px 22px",
      }}
    >
      <p
        className={`${jetMono.className} mb-5 text-[11px] font-medium uppercase tracking-[0.18em] text-[#64748B]`}
      >
        Подія №0001 – зібрано з модулів
      </p>
      <div className="flex flex-col items-center">
        {/* top block hops occasionally – the single ambient animation */}
        <motion.div
          className="w-full max-w-[240px]"
          style={{ transform: "translateX(8px)" }}
          animate={reduced ? undefined : { y: [0, -3, 0] }}
          transition={{ duration: 0.45, repeat: Infinity, repeatDelay: 4.2, ease: "easeInOut" }}
        >
          <Brick
            color={MODULES.bpr.color}
            textColor={MODULES.bpr.text}
            width="100%"
            height={42}
            label="БПР-автоматизація"
            price="1 200 ₴"
          />
        </motion.div>
        <div className="w-full max-w-[248px]" style={{ transform: "translateX(-6px)" }}>
          <Brick
            color={MODULES.catalog.color}
            textColor={MODULES.catalog.text}
            width="100%"
            height={42}
            label="Каталог CONFY"
            price="1 000 ₴"
          />
        </div>
        <div className="w-full max-w-[320px]">
          <Brick
            color={INK}
            width="100%"
            height={56}
            depth={13}
            label="Базова платформа"
            price="Безкоштовно"
          />
        </div>
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <span className={`${jetMono.className} text-[12px] text-[#64748B]`}>
          2 модулі + безкоштовний тариф
        </span>
        <span
          className={`${jetMono.className} rounded-full border-2 bg-white px-3 py-1 text-[13px] font-bold`}
          style={{ borderColor: INK }}
        >
          = 2 200 ₴
        </span>
      </div>
      <FreeOfferSticker className="-right-2 -top-9 w-[112px] sm:-right-4 sm:-top-11 sm:w-[136px] lg:-right-7 lg:-top-12 lg:w-[160px]" />
    </div>
  );
}

function HeroSection() {
  return (
    <section id="top" className="border-b-2" style={{ borderColor: INK }}>
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <Reveal>
            <p
              className={`${jetMono.className} mb-4 inline-flex items-center gap-2 rounded-full border-2 bg-white px-3.5 py-1.5 text-[12px] font-medium uppercase tracking-[0.14em]`}
              style={{ borderColor: INK }}
            >
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 rounded-[2px]"
                style={{ background: "#2FC98F" }}
              />
              Платформа для організаторів БПР-подій
            </p>
            <h1 className="text-4xl font-extrabold leading-[1.04] tracking-tight sm:text-5xl lg:text-[3.4rem]">
              {HERO.title}
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-[#4B4B55]">
              {HERO.subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-8 flex flex-wrap items-center gap-4">
            <ActivationCta
              className={`inline-flex items-center gap-2 rounded-full border-2 px-7 py-3.5 text-base font-bold text-white transition-transform hover:-translate-y-0.5 ${focusCls}`}
              style={{ borderColor: INK, background: INK, boxShadow: cardShadow }}
            >
              {HERO.ctaPrimary}
              <ArrowRight className="h-5 w-5" strokeWidth={2} />
            </ActivationCta>
            <a
              href="#estimator"
              className={`inline-flex items-center gap-2 rounded-full border-2 bg-white px-7 py-3.5 text-base font-bold transition-transform hover:-translate-y-0.5 ${focusCls}`}
              style={{ borderColor: INK, boxShadow: cardShadow }}
            >
              {HERO.ctaSecondary}
            </a>
          </Reveal>
          <Reveal delay={0.18} className="mt-10 flex items-center gap-3">
            <CountUp
              to={94000}
              format={formatAudience}
              className={`${jetMono.className} text-2xl font-bold tracking-tight`}
            />
            <span className="max-w-[240px] text-sm leading-snug text-[#4B4B55]">
              зареєстрованих медичних працівників уже на платформі
            </span>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="lg:col-span-5">
          <HeroStack />
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Why CONFY – outcomes as tidy bordered cards
// ---------------------------------------------------------------------------

function WhySection() {
  return (
    <section id="why" className="scroll-mt-24 py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeader
          kicker="Можливості"
          title="Чому CONFY?"
          sub="Кожен блок платформи знімає з вашої команди конкретний шматок роботи – від першої реєстрації до фінального звіту."
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          {WHY_ITEMS.map((item, i) => (
            <Reveal key={item.title} delay={Math.min(i * 0.05, 0.3)}>
              <div
                className="group h-full rounded-2xl border-2 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5"
                style={{ borderColor: INK, boxShadow: cardShadow }}
              >
                <div className="mb-3 flex items-center gap-2.5">
                  <span
                    aria-hidden
                    className="inline-block h-3.5 w-3.5 rounded-[3px] border-2"
                    style={{
                      borderColor: INK,
                      background: MODULE_COLOR_CYCLE[i % MODULE_COLOR_CYCLE.length],
                    }}
                  />
                  <span
                    className={`${jetMono.className} text-[12px] font-medium text-[#64748B]`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="text-[17px] font-bold leading-snug">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#4B4B55]">
                  {item.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Core platform – wall of interlocked tiles
// ---------------------------------------------------------------------------

function CoreSection() {
  return (
    <section
      className="border-y-2 py-16 sm:py-24"
      style={{ borderColor: INK, background: GROUND }}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeader
          kicker="Базова платформа"
          title="15 модулів. Все включено."
          sub="Базова платформа – це фундамент кожної події. Жодних штучних обмежень: усе перелічене входить у будь-який тариф, включно з безкоштовним."
        />
        <Reveal>
          <div
            className="overflow-hidden rounded-[18px] border-2 bg-white"
            style={{ borderColor: INK, boxShadow: cardShadow }}
          >
            <div className="flex flex-wrap pl-[2px] pt-[2px]">
              {CORE_FEATURES.map((feature, i) => (
                <div
                  key={feature}
                  className="-ml-[2px] -mt-[2px] flex w-full grow items-center gap-2.5 border-2 bg-white px-4 py-3.5 sm:w-auto"
                  style={{ borderColor: INK }}
                >
                  <span
                    aria-hidden
                    className="inline-block h-3 w-3 shrink-0 rounded-[3px] border-2"
                    style={{
                      borderColor: INK,
                      background: MODULE_COLOR_CYCLE[i % MODULE_COLOR_CYCLE.length],
                    }}
                  />
                  <span
                    className={`${jetMono.className} text-[11px] font-medium text-[#64748B]`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm font-semibold">{feature}</span>
                </div>
              ))}
              <div
                className="-ml-[2px] -mt-[2px] flex w-full grow items-center gap-2.5 border-2 px-4 py-3.5 sm:w-auto"
                style={{ borderColor: INK, background: INK }}
              >
                <Check className="h-4 w-4 shrink-0 text-white" strokeWidth={2} />
                <span className="text-sm font-bold text-white">
                  Усе включено в кожен тариф
                </span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Step 1 – tiers
// ---------------------------------------------------------------------------

function TiersSection() {
  const freeOffer = useFreeOffer();
  return (
    <section id="tiers" className="scroll-mt-24 py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeader
          kicker="Крок 1"
          title="Оберіть масштаб події"
          sub="Тариф залежить лише від кількості реєстрацій – усі можливості базової платформи однакові на кожному рівні."
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {TIERS.map((tier, i) => {
            const isFree = tier.id === "free";
            const isContact = tier.price === null;
            const CardTag = isContact ? "a" : "div";
            return (
              <Reveal key={tier.id} delay={Math.min(i * 0.06, 0.3)}>
                <CardTag
                  {...(isContact
                    ? {
                        href: `mailto:${CONTACT.email}?subject=${encodeURIComponent(
                          "Подія понад 50 000 реєстрацій"
                        )}`,
                      }
                    : {})}
                  className={`relative flex h-full flex-col rounded-2xl border-2 p-5 transition-all duration-200 hover:-translate-y-0.5 ${
                    isContact ? focusCls : ""
                  }`}
                  style={{
                    borderColor: INK,
                    background: isFree
                      ? tint("#FFB02E", 14)
                      : isContact
                        ? tint(INK, 5)
                        : "#FFFFFF",
                    boxShadow: cardShadow,
                  }}
                >
                  {isFree ? (
                    <span
                      className={`${jetMono.className} absolute -top-3.5 left-4 rounded-full border-2 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide`}
                      style={{ borderColor: INK, background: "#FFB02E", color: INK }}
                    >
                      Старт без ризику
                    </span>
                  ) : null}
                  <div className="mb-4 flex justify-center pt-2">
                    <Brick
                      color={TIER_COLORS[i] ?? INK}
                      width={96 + i * 22}
                      height={18 + i * 6}
                      depth={8}
                    />
                  </div>
                  <p
                    className={`${jetMono.className} text-center text-[12px] font-medium uppercase tracking-[0.14em] text-[#64748B]`}
                  >
                    {tier.limitLabel} реєстрацій
                  </p>
                  <p
                    className={`${jetMono.className} mt-2 text-center text-2xl font-bold tracking-tight`}
                  >
                    {tier.price === null ? "Індивідуально" : formatUAH(tier.price)}
                  </p>
                  <p className="mt-auto pt-3 text-center text-[13px] leading-snug text-[#4B4B55]">
                    {isContact ? (
                      <span className="font-semibold underline underline-offset-2">
                        Написати нам →
                      </span>
                    ) : (
                      (tier.note ??
                        (tier.price === 0
                          ? "Безкоштовно, назавжди"
                          : "За одну подію, разом з усією базовою платформою"))
                    )}
                  </p>
                </CardTag>
              </Reveal>
            );
          })}
        </div>
        {freeOffer ? (
          <Reveal className="mt-8 flex justify-center">
            <a
              href="#free-offer"
              className={`inline-flex flex-wrap items-center justify-center gap-2.5 rounded-2xl border-2 px-5 py-3 text-center text-sm font-semibold transition-transform hover:-translate-y-0.5 ${focusCls}`}
              style={{
                borderColor: INK,
                background: tint("#FFB02E", 16),
                boxShadow: cardShadow,
              }}
            >
              <span
                aria-hidden
                className="inline-block h-3 w-3 shrink-0 rounded-[3px] border-2"
                style={{ borderColor: INK, background: "#FFB02E" }}
              />
              {FREE_OFFER.tiersNote}
              <ArrowDown className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
            </a>
          </Reveal>
        ) : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Step 2 – add-on modules
// ---------------------------------------------------------------------------

function AddonCard({
  addon,
  index,
  wide,
}: {
  addon: Addon;
  index: number;
  wide?: boolean;
}) {
  const meta = MODULES[addon.id] ?? MODULES.marketing;
  return (
    <Reveal
      delay={Math.min(index * 0.06, 0.3)}
      className={`h-full ${wide ? "lg:col-span-3" : ""}`}
    >
      <div
        className={`flex h-full flex-col rounded-2xl border-2 p-5 transition-all duration-200 hover:-translate-y-0.5 ${
          wide ? "lg:flex-row lg:items-center lg:gap-7 lg:px-7" : ""
        }`}
        style={{
          borderColor: INK,
          background: tint(meta.color, 10),
          boxShadow: cardShadow,
        }}
      >
        <div
          className={`mb-4 flex items-start justify-between gap-3 ${
            wide ? "lg:mb-0 lg:shrink-0 lg:flex-col lg:items-start lg:justify-start lg:gap-3" : ""
          }`}
        >
          <Brick color={meta.color} width={72} height={26} depth={8} />
          <span
            className={`${jetMono.className} rounded-md border-2 bg-white px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.14em]`}
            style={{ borderColor: INK }}
          >
            Модуль {meta.num}
          </span>
        </div>
        <div className={wide ? "lg:min-w-0 lg:flex-1" : ""}>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-bold leading-snug">{addon.title}</h3>
            {addon.soon ? (
              <span
                className={`${jetMono.className} inline-flex items-center gap-1 rounded-full border-2 bg-white px-2 py-0.5 text-[11px] font-bold`}
                style={{ borderColor: INK }}
              >
                <Clock className="h-3 w-3" strokeWidth={2} />
                Скоро
              </span>
            ) : null}
          </div>
          <p
            className={`mt-2 text-sm leading-relaxed text-[#4B4B55] ${
              wide ? "lg:max-w-2xl" : ""
            }`}
          >
            {addon.desc}
          </p>
          {addon.options ? (
            <div className="mt-4 space-y-2">
              {addon.options.map((opt) => (
                <div
                  key={opt.id}
                  className="flex items-center justify-between rounded-xl border-2 bg-white px-3.5 py-2"
                  style={{ borderColor: INK }}
                >
                  <span className="text-sm font-semibold">{opt.label}</span>
                  <span className={`${jetMono.className} text-sm font-bold`}>
                    {formatUAH(opt.price)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className={`mt-auto pt-4 ${wide ? "lg:mt-0 lg:shrink-0 lg:pt-0" : ""}`}>
          {addon.id === "marketing" ? (
            <a
              href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(
                "Маркетингова кампанія на аудиторію CONFY"
              )}`}
              className={`${jetMono.className} inline-flex items-center gap-2 rounded-full border-2 px-3.5 py-1.5 text-sm font-bold underline underline-offset-2 transition-transform hover:-translate-y-0.5 ${focusCls}`}
              style={{ borderColor: INK, background: meta.color, color: meta.text }}
            >
              {addon.priceLabel} →
            </a>
          ) : (
            <span
              className={`${jetMono.className} inline-flex items-center gap-2 rounded-full border-2 px-3.5 py-1.5 text-sm font-bold`}
              style={{ borderColor: INK, background: meta.color, color: meta.text }}
            >
              {addon.priceLabel}
            </span>
          )}
        </div>
      </div>
    </Reveal>
  );
}

function AddonsSection() {
  return (
    <section
      id="addons"
      className="scroll-mt-24 border-t-2 py-16 sm:py-24"
      style={{ borderColor: INK }}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeader
          kicker="Крок 2"
          title="Додайте потрібні модулі"
          sub="Кожен модуль – окремий блок конструктора. Вмикайте лише те, що потрібно саме цій події."
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
          {ADDONS.map((addon, i) => (
            <AddonCard
              key={addon.id}
              addon={addon}
              index={i}
              wide={addon.id === "marketing"}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// PRO – flat-rate plan for frequent organizers (one payment, everything in)
// ---------------------------------------------------------------------------

// Full module stack shown pre-assembled: in PRO nothing is optional to buy.
const PRO_STACK: { id: keyof typeof MODULES; label: string; offset: number }[] = [
  { id: "bpr", label: "БПР-автоматизація", offset: 6 },
  { id: "payments", label: "Онлайн-оплати", offset: -4 },
  { id: "catalog", label: "Каталог CONFY", offset: -6 },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- temporarily removed from the page composition below
function ProSection() {
  return (
    <section id="pro" className="scroll-mt-24 pb-16 sm:pb-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <div
            className="overflow-hidden rounded-[18px] border-2 text-white"
            style={{ borderColor: INK, background: INK, boxShadow: cardShadow }}
          >
            <div className="grid gap-8 p-6 sm:p-10 lg:grid-cols-12 lg:gap-10">
              {/* Copy ---------------------------------------------------- */}
              <div className="flex flex-col lg:col-span-7">
                <p
                  className={`${jetMono.className} mb-4 inline-flex w-fit items-center gap-2 rounded-full border-2 border-white/40 px-3.5 py-1.5 text-[12px] font-medium uppercase tracking-[0.14em] text-white/90`}
                >
                  <span
                    aria-hidden
                    className="inline-block h-2.5 w-2.5 rounded-[2px]"
                    style={{ background: "#FFB02E" }}
                  />
                  {PRO_PLAN.kicker}
                </p>
                <h2 className="text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
                  {PRO_PLAN.title}
                </h2>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-white/70">
                  {PRO_PLAN.desc}
                </p>

                <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span
                    className={`${jetMono.className} text-4xl font-bold tracking-tight sm:text-5xl`}
                  >
                    {formatUAH(PRO_PLAN.priceMonthly)}
                  </span>
                  <span className="text-base font-semibold text-white/70">
                    на місяць
                  </span>
                  <span
                    className={`${jetMono.className} rounded-full border-2 px-3 py-1 text-[13px] font-bold`}
                    style={{
                      borderColor: "#2FC98F",
                      background: tint("#2FC98F", 90),
                      color: INK,
                    }}
                  >
                    = {formatUAH(PRO_PLAN.perEvent)} за подію
                  </span>
                </div>

                <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                  {PRO_PLAN.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 border-white/60"
                        style={{ background: "#2FC98F" }}
                      >
                        <Check
                          className="h-3 w-3"
                          strokeWidth={3}
                          style={{ color: INK }}
                        />
                      </span>
                      <span className="text-sm font-semibold leading-snug">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <a
                    href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(
                      PRO_PLAN.mailSubject
                    )}`}
                    className={`inline-flex items-center gap-2 rounded-full border-2 border-white bg-white px-7 py-3.5 text-base font-bold transition-transform hover:-translate-y-0.5 ${focusCls}`}
                    style={{ color: INK }}
                  >
                    <Mail className="h-5 w-5" strokeWidth={2} />
                    Підключити PRO
                  </a>
                  <p
                    className={`${jetMono.className} max-w-[300px] text-[12px] leading-relaxed text-white/60`}
                  >
                    {PRO_PLAN.note}
                  </p>
                </div>
              </div>

              {/* Pre-assembled stack ------------------------------------- */}
              <div className="lg:col-span-5">
                <div
                  className="relative flex h-full flex-col justify-end rounded-[18px] border-2 px-4 pb-7 pt-12 sm:px-8"
                  style={{
                    borderColor: INK,
                    background: GROUND,
                    backgroundImage:
                      "radial-gradient(#D8DAE1 1.2px, transparent 1.2px)",
                    backgroundSize: "22px 22px",
                    color: INK,
                  }}
                >
                  <p
                    className={`${jetMono.className} absolute left-5 top-4 text-[11px] font-medium uppercase tracking-[0.18em] text-[#64748B]`}
                  >
                    Подія в тарифі PRO – зібрано все
                  </p>
                  <div className="flex flex-col items-center">
                    {PRO_STACK.map((block) => {
                      const meta = MODULES[block.id];
                      return (
                        <div
                          key={block.id}
                          className="w-full max-w-[260px]"
                          style={{ transform: `translateX(${block.offset}px)` }}
                        >
                          <Brick
                            color={meta.color}
                            textColor={meta.text}
                            width="100%"
                            height={42}
                            label={block.label}
                            price="✓"
                          />
                        </div>
                      );
                    })}
                    <div className="w-full max-w-[320px]">
                      <Brick
                        color={INK}
                        width="100%"
                        height={56}
                        depth={13}
                        label="Базова платформа"
                        price="до 50 000"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                    <span className={`${jetMono.className} text-[12px] text-[#64748B]`}>
                      {PRO_PLAN.eventsPerMonth} таких подій на місяць
                    </span>
                    <span
                      className={`${jetMono.className} rounded-full border-2 bg-white px-3 py-1 text-[13px] font-bold`}
                      style={{ borderColor: INK }}
                    >
                      = {formatUAH(PRO_PLAN.priceMonthly)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Wallet + autoscale
// ---------------------------------------------------------------------------

const AUTOSCALE_STEP_COLORS = [INK, "#2F5BEA", "#FFB02E", "#2FC98F"];

function WalletSection() {
  return (
    <section
      className="border-y-2 py-16 sm:py-24"
      style={{ borderColor: INK, background: GROUND }}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeader
          kicker="Баланс і масштабування"
          title="Поповнили раз – далі все автоматично"
          sub="Баланс організатора та автоматичне підвищення тарифу тримають реєстрацію відкритою, поки ви займаєтесь подією."
        />
        <div className="grid gap-5 lg:grid-cols-2">
          <Reveal className="h-full">
            <div
              className="flex h-full flex-col rounded-[18px] border-2 bg-white p-6 sm:p-8"
              style={{ borderColor: INK, boxShadow: cardShadow }}
            >
              <div className="mb-4 flex items-center gap-3">
                <span
                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl border-2"
                  style={{ borderColor: INK, background: tint("#2FC98F", 18) }}
                >
                  <Wallet className="h-5 w-5" strokeWidth={2} />
                </span>
                <h3 className="text-xl font-bold">{WALLET.title}</h3>
              </div>
              <p className="text-sm leading-relaxed text-[#4B4B55]">{WALLET.desc}</p>
              <ul className="mt-5 space-y-3">
                {WALLET.points.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span
                      className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2"
                      style={{ borderColor: INK, background: "#2FC98F" }}
                    >
                      <Check className="h-3 w-3" strokeWidth={3} style={{ color: INK }} />
                    </span>
                    <span className="text-sm font-semibold leading-snug">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="h-full">
            <div
              className="flex h-full flex-col rounded-[18px] border-2 bg-white p-6 sm:p-8"
              style={{ borderColor: INK, boxShadow: cardShadow }}
            >
              <h3 className="text-xl font-bold">{AUTOSCALE.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#4B4B55]">
                {AUTOSCALE.desc}
              </p>
              <ol className="mt-6 space-y-0">
                {AUTOSCALE.example.map((step, i) => (
                  <li key={step} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <span
                        className={`${jetMono.className} inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-2 text-sm font-bold`}
                        style={{
                          borderColor: INK,
                          background: AUTOSCALE_STEP_COLORS[i] ?? INK,
                          color: i === 0 || i === 1 ? "#FFFFFF" : INK,
                        }}
                      >
                        {i + 1}
                      </span>
                      {i < AUTOSCALE.example.length - 1 ? (
                        <span
                          aria-hidden
                          className="my-1 w-[2px] flex-1"
                          style={{ background: INK, minHeight: 18 }}
                        />
                      ) : null}
                    </div>
                    <div className="pb-5 pt-1.5">
                      <p
                        className={
                          i === AUTOSCALE.example.length - 1
                            ? `${jetMono.className} text-[15px] font-bold`
                            : "text-[15px] font-semibold"
                        }
                      >
                        {step}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
              <p
                className={`${jetMono.className} mt-auto rounded-xl border-2 px-4 py-3 text-[13px] leading-relaxed`}
                style={{ borderColor: INK, background: tint("#2F5BEA", 10) }}
              >
                Реєстрація не зупинилась ані на секунду.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Estimator – the signature block-stacking constructor
// ---------------------------------------------------------------------------

type StackBlockDef = {
  id: string;
  color: string;
  textColor: string;
  label: string;
  price: string;
  offset: number;
};

const STACK_OFFSETS: Record<string, number> = {
  catalog: -6,
  payments: -4,
  bpr: 6,
};

// Narrowest slab must still fit «Базова платформа» + the tier label untruncated.
const TIER_SLAB_WIDTH = [248, 272, 296, 320, 344];
const TIER_SLAB_HEIGHT = [40, 46, 52, 58, 62];

function ToggleSwitch({
  checked,
  color,
  reduced,
}: {
  checked: boolean;
  color: string;
  reduced: boolean;
}) {
  return (
    <span
      aria-hidden
      className="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border-2 transition-colors"
      style={{ borderColor: INK, background: checked ? color : "#E2E4E9" }}
    >
      <motion.span
        className="absolute left-0.5 h-5 w-5 rounded-full border-2 bg-white"
        style={{ borderColor: INK }}
        animate={{ x: checked ? 20 : 0 }}
        transition={
          reduced ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 30 }
        }
      />
    </span>
  );
}

function EstimatorSection() {
  const reduced = useReducedMotion() ?? false;
  // Default configuration: free event (up to 100) + catalog publication only,
  // so the first total a visitor sees is 1 000 ₴.
  const [tierId, setTierId] = useState<string>("free");
  const [addons, setAddons] = useState<EstimatorAddonState>({
    catalog: true,
    email: false,
    payments: false,
    bpr: false,
    qr: false,
  });

  const tierIndex = Math.max(
    TIERS.findIndex((t) => t.id === tierId),
    0
  );
  const tier: Tier = TIERS[tierIndex] ?? TIERS[0];
  const total = estimateTotal(tier, addons);

  // Animated total ----------------------------------------------------------
  const totalMV = useMotionValue(total ?? 0);
  const [displayTotal, setDisplayTotal] = useState<number>(total ?? 0);
  useEffect(() => {
    if (total === null) return;
    const controls = animate(totalMV, total, {
      duration: reduced ? 0 : 0.5,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplayTotal(Math.round(v)),
    });
    return () => controls.stop();
  }, [total, reduced, totalMV]);

  // Stack blocks, top → bottom (base slab rendered last = at the bottom) ----
  const stackBlocks = useMemo<StackBlockDef[]>(() => {
    const defs: StackBlockDef[] = [];
    const push = (id: string, label: string, price: string) => {
      const meta = MODULES[id];
      if (!meta) return;
      defs.push({
        id,
        color: meta.color,
        textColor: meta.text,
        label,
        price,
        offset: STACK_OFFSETS[id] ?? 0,
      });
    };
    if (addons.bpr) push("bpr", "БПР-автоматизація", "1 200 ₴");
    if (addons.payments) push("payments", "Онлайн-оплати", "3,8% з продажів");
    if (addons.catalog) push("catalog", "Каталог CONFY", "1 000 ₴");
    return defs;
  }, [addons]);

  const blockTransition = reduced
    ? { duration: 0 }
    : {
        y: { type: "spring" as const, stiffness: 300, damping: 20 },
        scaleY: { duration: 0.5, delay: 0.06, times: [0, 0.55, 1], ease: "easeOut" as const },
        opacity: { duration: 0.18 },
        layout: { type: "spring" as const, stiffness: 300, damping: 26 },
      };

  const setAddon = (patch: Partial<EstimatorAddonState>) =>
    setAddons((prev) => ({ ...prev, ...patch }));

  const toggleRows: {
    id: keyof EstimatorAddonState;
    title: string;
    priceLabel: string;
    checked: boolean;
    soon?: boolean;
    onToggle: () => void;
  }[] = [
    {
      id: "catalog",
      title: "Публікація в каталозі CONFY",
      priceLabel: "1 000 ₴",
      checked: addons.catalog,
      onToggle: () => setAddon({ catalog: !addons.catalog }),
    },
    {
      id: "payments",
      title: "Онлайн-оплати",
      priceLabel: "3,8% з продажів",
      checked: addons.payments,
      onToggle: () => setAddon({ payments: !addons.payments }),
    },
    {
      id: "bpr",
      title: "БПР-автоматизація",
      priceLabel: "1 200 ₴",
      checked: addons.bpr !== false,
      onToggle: () => setAddon({ bpr: addons.bpr === false ? "standard" : false }),
    },
  ];

  return (
    <section id="estimator" className="scroll-mt-24 py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeader
          kicker="Конструктор вартості"
          title="Зберіть свою подію"
          sub="Оберіть масштаб, увімкніть модулі – і подивіться, як складається ваша подія та її вартість."
        />
        <Reveal>
          <div className="grid gap-5 lg:grid-cols-12 lg:gap-6">
            {/* Controls – self-start so the card hugs its content instead of
                stretching to the (taller) stack column and showing dead space */}
            <div
              className="rounded-[18px] border-2 bg-white p-5 sm:p-7 lg:col-span-6 lg:self-start"
              style={{ borderColor: INK, boxShadow: cardShadow }}
            >
              <p
                className={`${jetMono.className} mb-3 text-[12px] font-medium uppercase tracking-[0.18em] text-[#64748B]`}
              >
                Крок 1 – Масштаб
              </p>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Масштаб події">
                {TIERS.map((t) => {
                  const active = t.id === tierId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setTierId(t.id)}
                      className={`rounded-xl border-2 px-3.5 py-2 text-left transition-all duration-150 hover:-translate-y-0.5 ${focusCls}`}
                      style={{
                        borderColor: INK,
                        background: active ? INK : "#FFFFFF",
                        color: active ? "#FFFFFF" : INK,
                        boxShadow: active ? cardShadow : "none",
                      }}
                    >
                      <span className="block text-[13px] font-bold leading-tight">
                        {t.limitLabel}
                      </span>
                      <span
                        className={`${jetMono.className} block text-[11px] font-medium opacity-80`}
                      >
                        {t.price === null ? "за запитом" : formatUAH(t.price)}
                      </span>
                    </button>
                  );
                })}
              </div>
              {tier.id === "free" ? (
                <p className="mt-3 text-[13px] leading-snug text-[#4B4B55]">
                  {addons.catalog
                    ? "З модулем «Публікація в каталозі» подія стає публічною і видимою аудиторії CONFY."
                    : tier.note}
                </p>
              ) : tier.note ? (
                <p className="mt-3 text-[13px] leading-snug text-[#4B4B55]">
                  {tier.note}
                </p>
              ) : null}

              <p
                className={`${jetMono.className} mb-3 mt-7 text-[12px] font-medium uppercase tracking-[0.18em] text-[#64748B]`}
              >
                Крок 2 – Модулі
              </p>
              <div className="space-y-2.5">
                {toggleRows.map((row) => {
                  const meta = MODULES[row.id as string] ?? MODULES.marketing;
                  return (
                    <div
                      key={row.id as string}
                      className="rounded-2xl border-2"
                      style={{
                        borderColor: INK,
                        background: row.checked ? tint(meta.color, 12) : "#FFFFFF",
                      }}
                    >
                      <button
                        type="button"
                        role="switch"
                        aria-checked={row.checked}
                        onClick={row.onToggle}
                        className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left ${focusCls}`}
                      >
                        <ToggleSwitch
                          checked={row.checked}
                          color={meta.color}
                          reduced={reduced}
                        />
                        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="text-sm font-bold leading-tight">
                            {row.title}
                          </span>
                          {row.soon ? (
                            <span
                              className={`${jetMono.className} rounded-full border-2 bg-white px-1.5 py-px text-[10px] font-bold`}
                              style={{ borderColor: INK }}
                            >
                              Скоро
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={`${jetMono.className} shrink-0 text-[13px] font-bold`}
                        >
                          {row.priceLabel}
                        </span>
                      </button>
                    </div>
                  );
                })}
                {/* Marketing campaigns: same row styling, no toggle – priced on request */}
                <a
                  href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(
                    "Маркетингова кампанія на аудиторію CONFY"
                  )}`}
                  className={`flex w-full items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-all duration-150 hover:-translate-y-0.5 ${focusCls}`}
                  style={{
                    borderColor: INK,
                    background: tint(MODULES.marketing.color, 8),
                  }}
                >
                  <Mail
                    className="h-5 w-5 shrink-0"
                    strokeWidth={2}
                    style={{ color: MODULES.marketing.color }}
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-sm font-bold leading-tight">
                      Маркетингові кампанії
                    </span>
                    <span className="text-[12px] text-[#4B4B55]">
                      на аудиторію CONFY
                    </span>
                  </span>
                  <span
                    className={`${jetMono.className} shrink-0 text-[13px] font-bold underline underline-offset-2`}
                  >
                    За запитом →
                  </span>
                </a>
              </div>
            </div>

            {/* Live stack ------------------------------------------------ */}
            <div className="relative flex flex-col gap-4 lg:col-span-6">
              <FreeOfferSticker className="-right-1 -top-8 w-[104px] sm:-right-3 sm:-top-9 sm:w-[122px] lg:-right-4 lg:-top-10 lg:w-[136px]" />
              <div
                className="relative flex flex-1 flex-col justify-end overflow-hidden rounded-[18px] border-2 px-4 pb-8 pt-10 sm:px-8"
                style={{
                  borderColor: INK,
                  background: GROUND,
                  boxShadow: cardShadow,
                  backgroundImage:
                    "radial-gradient(#D8DAE1 1.2px, transparent 1.2px)",
                  backgroundSize: "22px 22px",
                  // Fits the tallest possible stack (3 modules + largest tier
                  // slab) with a little headroom – was 420 when 5 modules existed.
                  minHeight: 320,
                }}
              >
                <p
                  className={`${jetMono.className} absolute left-5 top-4 text-[11px] font-medium uppercase tracking-[0.18em] text-[#64748B]`}
                >
                  Ваша подія
                </p>
                <div className="flex flex-col items-center">
                  <AnimatePresence initial={false}>
                    {stackBlocks.length === 0 ? (
                      <motion.div
                        key="empty-hint"
                        initial={reduced ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.15 } }}
                        className="mb-2 w-full max-w-[250px]"
                      >
                        <div
                          className={`${jetMono.className} rounded-xl border-2 border-dashed px-4 py-3 text-center text-[12px] text-[#64748B]`}
                          style={{ borderColor: "#9AA0AC" }}
                        >
                          + увімкніть модулі
                        </div>
                      </motion.div>
                    ) : null}
                    {stackBlocks.map((block) => (
                      <motion.div
                        key={block.id}
                        layout
                        initial={reduced ? false : { y: -120, opacity: 0 }}
                        animate={
                          reduced
                            ? { y: 0, opacity: 1 }
                            : { y: 0, opacity: 1, scaleY: [1, 0.88, 1] }
                        }
                        exit={
                          reduced
                            ? { opacity: 0, transition: { duration: 0 } }
                            : {
                                y: -70,
                                opacity: 0,
                                transition: { duration: 0.22, ease: "easeIn" },
                              }
                        }
                        transition={blockTransition}
                        style={{ originY: 1, width: "100%", maxWidth: 270 }}
                      >
                        <div style={{ transform: `translateX(${block.offset}px)` }}>
                          <Brick
                            color={block.color}
                            textColor={block.textColor}
                            width="100%"
                            height={44}
                            label={block.label}
                            price={block.price}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  <motion.div
                    layout
                    transition={
                      reduced
                        ? { duration: 0 }
                        : { type: "spring", stiffness: 300, damping: 26 }
                    }
                    style={{
                      width: "100%",
                      maxWidth: TIER_SLAB_WIDTH[tierIndex] ?? 344,
                    }}
                  >
                    <Brick
                      color={INK}
                      width="100%"
                      height={TIER_SLAB_HEIGHT[tierIndex] ?? 62}
                      depth={13}
                      label="Базова платформа"
                      price={tier.limitLabel}
                    />
                  </motion.div>
                </div>
              </div>

              {/* Total plate */}
              <div
                className="rounded-[18px] border-2 bg-white px-5 py-4 sm:px-7 sm:py-5"
                style={{ borderColor: INK, boxShadow: cardShadow }}
                aria-live="polite"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <span className="text-base font-bold sm:text-lg">
                    Разом за подію:
                  </span>
                  {total === null ? (
                    <a
                      href={`mailto:${CONTACT.email}`}
                      className={`${jetMono.className} text-lg font-bold underline sm:text-xl ${focusCls} rounded-sm`}
                    >
                      Звʼяжіться з нами
                    </a>
                  ) : (
                    <span
                      className={`${jetMono.className} text-3xl font-bold tracking-tight sm:text-4xl`}
                    >
                      {formatUAH(displayTotal)}
                    </span>
                  )}
                </div>
                {addons.payments ? (
                  <p
                    className={`${jetMono.className} mt-1 text-right text-[13px] font-medium text-[#4B4B55]`}
                  >
                    + 3,8% з онлайн-продажів квитків
                  </p>
                ) : null}
                <p className="mt-2 text-[13px] leading-snug text-[#4B4B55]">
                  Ціна – за одну подію. Оплата списується з балансу організатора.
                </p>
              </div>

              {/* Act on the configuration right where it was assembled */}
              <ActivationCta
                className={`inline-flex w-full items-center justify-center gap-2 rounded-full border-2 px-7 py-4 text-base font-bold text-white transition-transform hover:-translate-y-0.5 ${focusCls}`}
                style={{ borderColor: INK, background: INK, boxShadow: cardShadow }}
              >
                Створити подію
                <ArrowRight className="h-5 w-5" strokeWidth={2} />
              </ActivationCta>
              <p className="-mt-1 text-center text-[13px] leading-snug text-[#4B4B55]">
                Модулі можна увімкнути або вимкнути будь-коли після створення.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Future add-ons
// ---------------------------------------------------------------------------

function FutureSection() {
  return (
    <section
      className="border-t-2 py-16 sm:py-20"
      style={{ borderColor: INK, background: GROUND }}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeader
          kicker="Roadmap"
          title="Попереду більше"
          sub="Нові блоки конструктора, над якими ми вже працюємо."
        />
        <Reveal>
          <ul className="flex flex-wrap justify-center gap-2.5 sm:gap-3">
            {FUTURE_ADDONS.map((item, i) => (
              <li key={item}>
                <span
                  className="inline-flex items-center gap-2 rounded-full border-2 bg-white px-4 py-2 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                  style={{ borderColor: INK, boxShadow: cardShadow }}
                >
                  <Plus
                    className="h-3.5 w-3.5"
                    strokeWidth={2.5}
                    style={{
                      color: MODULE_COLOR_CYCLE[i % MODULE_COLOR_CYCLE.length],
                    }}
                  />
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Free offer – first 10 events free for new organizers (details block, the
// anchor target of every «Перші 10 подій – безкоштовно*» badge on the page)
// ---------------------------------------------------------------------------

function FreeOfferSection() {
  const freeOffer = useFreeOffer();
  if (!freeOffer) return null;
  return (
    <section
      id="free-offer"
      className="scroll-mt-24 border-t-2 py-16 sm:py-24"
      style={{ borderColor: INK }}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <div
            className="rounded-[18px] border-2 p-6 text-center sm:p-12"
            style={{
              borderColor: INK,
              background: tint("#FFB02E", 16),
              boxShadow: cardShadow,
            }}
          >
            {/* 10 blocks = 10 free events */}
            <div aria-hidden className="mb-7 flex flex-wrap justify-center gap-[4px]">
              {Array.from({ length: 10 }).map((_, i) => (
                <span
                  key={i}
                  className="inline-block h-4 w-4 rounded-[3px] border-2 sm:h-5 sm:w-5"
                  style={{ borderColor: INK, background: "#FFB02E" }}
                />
              ))}
            </div>
            <p
              className={`${jetMono.className} mb-3 text-[12px] font-medium uppercase tracking-[0.18em] text-[#64748B]`}
            >
              {FREE_OFFER.kicker}
            </p>
            <h2 className="mx-auto max-w-2xl text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
              {FREE_OFFER.title}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#4B4B55] sm:text-lg">
              {FREE_OFFER.desc}
            </p>
            <ul className="mx-auto mt-7 flex max-w-3xl flex-wrap justify-center gap-x-8 gap-y-3">
              {FREE_OFFER.points.map((point) => (
                <li key={point} className="flex items-center gap-2.5">
                  <span
                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2"
                    style={{ borderColor: INK, background: "#2FC98F" }}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} style={{ color: INK }} />
                  </span>
                  <span className="text-sm font-semibold leading-snug">{point}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex justify-center">
              <ActivationCta
                className={`inline-flex items-center gap-2 rounded-full border-2 px-7 py-3.5 text-base font-bold text-white transition-transform hover:-translate-y-0.5 ${focusCls}`}
                style={{ borderColor: INK, background: INK, boxShadow: cardShadow }}
              >
                {FREE_OFFER.cta}
                <ArrowRight className="h-5 w-5" strokeWidth={2} />
              </ActivationCta>
            </div>
            <p
              className={`${jetMono.className} mt-6 text-[12px] leading-relaxed text-[#64748B]`}
            >
              {FREE_OFFER.terms}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Final CTA + footer
// ---------------------------------------------------------------------------

function FooterSection() {
  const freeOffer = useFreeOffer();
  return (
    <footer className="border-t-2 text-white" style={{ borderColor: INK, background: INK }}>
      <div className="mx-auto max-w-6xl px-4 py-16 text-center sm:px-6 sm:py-24">
        <Reveal>
          <div aria-hidden className="mb-8 flex justify-center gap-[3px]">
            {["#2F5BEA", "#FF6B57", "#2FC98F", "#FFB02E", "#8B5CF6"].map((c, i) => (
              <span
                key={c}
                className="inline-block w-6 rounded-[4px] border-2 border-white/80"
                style={{ background: c, height: 14 + ((i * 7) % 12) }}
              />
            ))}
          </div>
          <h2 className="mx-auto max-w-2xl text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
            Створіть свою першу подію вже сьогодні
          </h2>
          {freeOffer ? (
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/70">
              Почніть із{" "}
              <a
                href="#free-offer"
                aria-label="10 безкоштовних подій – умови пропозиції для нових провайдерів"
                className={`font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors hover:decoration-white ${focusCls} rounded-sm`}
              >
                10 безкоштовних подій*
              </a>{" "}
              – до 50 000 реєстрацій і всі модулі вже включено.
            </p>
          ) : (
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/70">
              Реєстрація, тестування, сертифікати та БПР – усе на одній
              платформі, до 50 000 реєстрацій на подію.
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <ActivationCta
              className={`inline-flex items-center gap-2 rounded-full border-2 border-white bg-white px-7 py-3.5 text-base font-bold transition-transform hover:-translate-y-0.5 ${focusCls}`}
              style={{ color: INK }}
            >
              Створити першу подію безкоштовно
              <ArrowRight className="h-5 w-5" strokeWidth={2} />
            </ActivationCta>
            <a
              href={`mailto:${CONTACT.email}`}
              className={`inline-flex items-center gap-2 rounded-full border-2 border-white/60 px-7 py-3.5 text-base font-bold text-white transition-transform hover:-translate-y-0.5 ${focusCls}`}
            >
              <Mail className="h-5 w-5" strokeWidth={2} />
              Написати нам
            </a>
          </div>
        </Reveal>
      </div>
      <div className="border-t-2 border-white/15">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm text-white/70 sm:flex-row sm:px-6">
          <span className="font-extrabold tracking-tight text-white">CONFY</span>
          <div className={`${jetMono.className} flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px]`}>
            <a
              href={`mailto:${CONTACT.email}`}
              className={`hover:text-white hover:underline ${focusCls} rounded-sm`}
            >
              {CONTACT.email}
            </a>
            <a
              href={CONTACT.platformUrl}
              className={`hover:text-white hover:underline ${focusCls} rounded-sm`}
            >
              confy.cc
            </a>
          </div>
          <span className={`${jetMono.className} text-[13px]`}>
            © 2026 CONFY
          </span>
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Mobile arrow between steps (decorative)
// ---------------------------------------------------------------------------

function StepConnector() {
  return (
    <div aria-hidden className="flex justify-center pb-2">
      <span
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 bg-white"
        style={{ borderColor: INK, boxShadow: cardShadow }}
      >
        <ArrowDown className="h-5 w-5" strokeWidth={2} />
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function OrganizersV8Page() {
  return (
    <div className={`${rubik.className} overflow-x-clip bg-white text-[#16161C] antialiased`}>
      <FreeOfferProvider>
      <ActivationProvider>
        <TopNav />
        <main>
        <HeroSection />
        <WhySection />
        <CoreSection />
        <TiersSection />
        <StepConnector />
        <AddonsSection />
        {/* Temporarily hidden PRO flat-rate plan: <ProSection /> */}
        <WalletSection />
        <EstimatorSection />
          <FutureSection />
          <FreeOfferSection />
        </main>
        <FooterSection />
        <WelcomeVideoModal />
      </ActivationProvider>
      </FreeOfferProvider>
    </div>
  );
}
